import "./OutcomeInfo.scss";

import { isAxiosError } from "axios";
import { type FC, useEffect, useRef, useState } from "react";

import { SectionOutcomeAverageScore } from "./SectionOutcomeAverageScore";
import { displayOutcomeName, validateOutcomeName } from "./helpers";

import { HoverableEditableText } from "components/hover-widgets/EditableText";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { RubricList } from "components/outcomes/RubricList";
import { OutcomeType, RubricType } from "components/server-types";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCanAccessStudentSectionScores, useCapabilities } from "store/capability-hooks";
import { storeApi } from "store/index";
import { NestedSectionOutcomeType } from "store/selectors";
import { toastErrorMessage, toastLocalizedOperationCatcher } from "utils/alerts";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

type OutcomeInfoProps = {
  readOnly?: boolean;
  createNewOutcome?: () => void;
  rubric: RubricType | null;
} & (
  | {
      /**
       * This component is used both by the course-level and org-level outcomes pages,
       * and the logic is slightly different between the two.
       */
      isSectionLevel: true;
      outcomeItem: NestedSectionOutcomeType | undefined;
    }
  | {
      isSectionLevel: false;
      outcomeItem: OutcomeType | undefined;
    }
);

export const OutcomeInfo: FC<OutcomeInfoProps> = ({
  outcomeItem,
  readOnly,
  createNewOutcome,
  rubric,
  isSectionLevel,
}) => {
  const { canAuthorSection } = useCapabilities();
  const canAccessStudentSectionScores = useCanAccessStudentSectionScores();
  const [isEditingEmptyDescription, setIsEditingEmptyDescription] = useState(false);
  const isSmallScreen = !useIsMdOrLarger();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSmallScreen && outcomeItem?.id && containerRef.current) {
      // Find the nearest scrollable parent and scroll only that container
      const scrollableParent = containerRef.current.closest(".overflow-y-auto");
      if (scrollableParent) {
        const containerTop = containerRef.current.offsetTop;
        scrollableParent.scrollTo({ top: containerTop, behavior: "smooth" });
      }
    }
  }, [isSmallScreen, outcomeItem?.id]);

  if (createNewOutcome) {
    return (
      <div className="flex h-full w-full justify-center text-black-tint-40">
        <div className="mt-[200px]">{t("outcomes.no_outcomes")}</div>
      </div>
    );
  }

  if (!outcomeItem) {
    return isSmallScreen ? null : (
      <div className="flex h-full w-full justify-center text-black-tint-40">
        <div className="mt-[200px]">{t("outcome_info.no_outcome_selected")}</div>
      </div>
    );
  }

  const onOutcomeDescriptionChange = (newDescription: string) => {
    setIsEditingEmptyDescription(false);
    storeApi.outcomes.partial_update({
      id: "outcome_id" in outcomeItem ? outcomeItem.outcome_id : outcomeItem.id,
      description: newDescription,
    });
  };

  const outcome = "outcome" in outcomeItem ? outcomeItem.outcome : outcomeItem;
  // TODO: At some future point we need to lookup and return a string array with the previous names (if any).
  // react-doctor-disable-next-line prefer-module-scope-static-value
  const previousNames: string[] = [];
  const showPreviousNames = previousNames.length > 0;

  const onOutcomeTitleChange = (title: string) => {
    storeApi.outcomes
      .partial_update({ id: outcome.id, title }, { skipToast: true })
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 400) {
          const data = error.response?.data as { title?: string[] | string };
          const titleErrMsg = Array.isArray(data?.title) ? data.title[0] : data?.title;

          if (titleErrMsg === "err_outcome_title_already_exists") {
            toastErrorMessage(t("error.toasts.outcome_title_already_exists"));
            return;
          }
        }

        toastLocalizedOperationCatcher("update_failure", {
          item: t("glossary.outcomes"),
        })(error);
      });

    trackEvent("Outcome - Change title", { ...GENERAL_EVENT_CATEGORY });
  };

  const editableTitle = (
    <div className="flex w-full items-start justify-start">
      <span
        className={
          isSmallScreen
            ? "h2 my-0 flex h-[38px] items-center"
            : "h2 my-0 flex h-[42px] items-center"
        }
      >
        #
      </span>
      <HoverableToolbar
        className="flex w-full min-w-12 items-center justify-between"
        contentClassName="bg-white"
        readOnly={readOnly}
        reference="outcome"
        showEditButton
      >
        <h2 className="my-0 flex w-full items-center">
          <HoverableEditableText
            className="outcome-title pl-2"
            label={t("glossary.outcome_title")}
            textSize={isSmallScreen ? "h3" : "h2"}
            validatorFn={validateOutcomeName}
            value={outcome.title}
            onValueChanged={onOutcomeTitleChange}
          />
        </h2>
      </HoverableToolbar>
    </div>
  );

  return (
    <div className="page flex flex-col gap-3" ref={containerRef}>
      <div className="items-centers flex justify-between">
        {isSectionLevel ? (
          canAuthorSection ? (
            <h2 className="flex items-center gap-2">
              <OutcomeChip role="heading" title={displayOutcomeName(outcome)} />
            </h2>
          ) : canAccessStudentSectionScores ? (
            <h2 className="flex items-center gap-2">
              <SectionOutcomeAverageScore role="heading" sectionOutcome={outcomeItem} />
            </h2>
          ) : (
            <h2 className="flex items-center gap-2">
              <OutcomeChip role="heading" title={displayOutcomeName(outcome)} />
            </h2>
          )
        ) : (
          editableTitle
        )}

        {!readOnly && showPreviousNames && (
          <div className="body-s flex items-center gap-3 text-black-tint-40">
            <div className="bold">{t("outcomes.previous_names_list")}</div>
            <div className="body-xs">{previousNames.join(", ")}</div>
          </div>
        )}
      </div>

      <div>
        {!readOnly && outcome.description === "" && !isEditingEmptyDescription ? (
          <div className="mb-4 flex h-[38px] items-center">
            <HideUnlessEditModeButton
              reference="outcome_description"
              onClick={() => setIsEditingEmptyDescription(true)}
            />
          </div>
        ) : readOnly && outcome.description === "" ? null : (
          <div className="mb-4 min-h-[38px]">
            <HoverableToolbar
              className="min-h-[38px]"
              readOnly={readOnly}
              reference="outcome_description"
              showEditButton
            >
              <HoverableEditableText
                className="text-black-tint-40"
                editingOnStart={isEditingEmptyDescription}
                label={t("glossary.outcome_description")}
                textSize="s"
                value={outcome.description ?? ""}
                multiline
                onValueChanged={onOutcomeDescriptionChange}
              />
            </HoverableToolbar>
          </div>
        )}

        {/* TODO: Fix the jiggling that happens when editing the page outcome rubrics. */}
        {rubric && (
          <div className="shrink grow-[2] basis-[300px]">
            <RubricList
              key={outcomeItem.id}
              outcomeItem={outcomeItem}
              readOnly={readOnly}
              rubric={rubric}
            />
          </div>
        )}
      </div>
    </div>
  );
};
